import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  AdminPanelSettingsRounded,
  BlockRounded,
  Close,
  EditOutlined,
  PersonRounded,
  RefreshRounded,
  SearchRounded,
  VisibilityOutlined,
  VerifiedRounded,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import AdminHeader from "../../AdminHeader";
import AddUserModel from "../models/AddUserModel";
import { useAuth } from "../../../auth/useAuth";
import { APP_API_V1_BASE } from "../../../config/api";

const USERS_API = `${APP_API_V1_BASE}/users`;
const TEXT_PRIMARY = "#111827";
const TEXT_MUTED = "#667085";

const normalizeRoleForUi = (role) => role || "user";

const roleTone = (role) =>
  role === "superadmin"
    ? { color: "#6d28d9", bg: "#f3e8ff", border: "#d8b4fe", label: "Superadmin" }
    : { color: "#334155", bg: "#f8fafc", border: "#cbd5e1", label: "Platform User" };

const statusTone = (status) =>
  status === "active"
    ? { color: "#166534", bg: "#dcfce7", border: "#86efac", label: "Active" }
    : { color: "#475569", bg: "#f1f5f9", border: "#cbd5e1", label: "Suspended" };

export default function Users() {
  const { user } = useAuth();
  const isSuperAdmin = user?.global_role === "superadmin";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const [filters, setFilters] = useState({
    email: "",
    status: "",
    global_role: "",
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);

  const token = localStorage.getItem("authToken");
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );

  const formatActivityTime = (value) => {
    if (!value) return "Never";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Never";

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
      };

      if (filters.email.trim()) params.email = filters.email.trim();
      if (filters.status) params.status = filters.status;
      if (filters.global_role) params.global_role = filters.global_role;

      const res = await axios.get(USERS_API, { headers, params });
      setUsers(res.data?.data || []);
      setRowCount(res.data?.meta?.total || 0);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load platform users");
    } finally {
      setLoading(false);
    }
  }, [filters, headers, paginationModel]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const showUserDetail = async (targetUser) => {
    try {
      setError("");
      setDetailOpen(true);
      setDetailLoading(true);
      setUserDetail(null);

      const res = await axios.get(`${USERS_API}/${targetUser.id}`, { headers });
      setUserDetail(res.data);
    } catch (err) {
      setDetailOpen(false);
      setError(err?.response?.data?.error || "Failed to load user details");
    } finally {
      setDetailLoading(false);
    }
  };

  const updateUserRole = async (targetUser, nextRole) => {
    try {
      setError("");
      const payload = {
        global_role: nextRole === "user" ? null : nextRole,
      };

      const res = await axios.post(
        `${USERS_API}/${targetUser.id}/global-role`,
        payload,
        { headers },
      );

      const updatedRole = res.data?.user?.global_role || null;
      setUsers((prev) =>
        prev.map((row) =>
          row.id === targetUser.id
            ? { ...row, global_role: updatedRole }
            : row,
        ),
      );
      setSuccess("Global role updated");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update global role");
    }
  };

  const updateUserStatus = async (targetUser, checked) => {
    try {
      setError("");
      const status = checked ? "active" : "suspended";

      const res = await axios.patch(
        `${USERS_API}/${targetUser.id}/status`,
        { status },
        { headers },
      );

      const updatedStatus = res.data?.user?.status || status;
      setUsers((prev) =>
        prev.map((row) =>
          row.id === targetUser.id
            ? { ...row, status: updatedStatus }
            : row,
        ),
      );
      setSuccess(`User ${updatedStatus === "active" ? "activated" : "suspended"}`);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update user status");
    }
  };

  const updateUserPassword = async (targetUser, password) => {
    try {
      setError("");
      await axios.post(
        `${USERS_API}/${targetUser.id}/password`,
        { password },
        { headers },
      );
      setSuccess("Password updated");
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update user password");
      throw err;
    }
  };

  const handleSaveUser = async (form) => {
    try {
      setSaving(true);
      setError("");

      if (selectedUser) {
        if (normalizeRoleForUi(selectedUser.global_role) !== form.global_role) {
          await updateUserRole(selectedUser, form.global_role);
        }

        if ((selectedUser.status || "active") !== form.status) {
          await updateUserStatus(selectedUser, form.status === "active");
        }

        if ((form.password || "").trim()) {
          await updateUserPassword(selectedUser, form.password.trim());
        }

        await fetchUsers();
        setSuccess("Platform user updated");
      } else {
        const payload = {
          name: form.name,
          email: form.email,
          password: form.password,
          global_role: form.global_role,
          status: form.status,
        };

        await axios.post(USERS_API, payload, { headers });
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        await fetchUsers();
        setSuccess("Platform user created");
      }

      setOpenDialog(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to save platform user");
    } finally {
      setSaving(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const resetFilters = () => {
    setFilters({ email: "", status: "", global_role: "" });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const stats = useMemo(() => {
    const active = users.filter((row) => row.status === "active").length;
    const superadmins = users.filter(
      (row) => row.global_role === "superadmin",
    ).length;

    return {
      total: rowCount || users.length,
      active,
      suspended: users.length - active,
      superadmins,
    };
  }, [rowCount, users]);

  const columns = [
    {
      field: "s_no",
      headerName: "S.No.",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        paginationModel.page * paginationModel.pageSize +
        params.api.getAllRowIds().indexOf(params.id) +
        1,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Stack spacing={0.35} justifyContent="center">
          <Typography
            sx={{
              fontWeight: 700,
              color: TEXT_PRIMARY,
              letterSpacing: "-0.01em",
            }}
          >
            {params.value || "-"}
          </Typography>
          <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
            {params.row.email || "No email assigned"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "global_role",
      headerName: "Global Role",
      minWidth: 170,
      renderCell: (params) => {
        const row = params.row;
        const value = normalizeRoleForUi(params.value);

        return isSuperAdmin ? (
          <Select
            size="small"
            value={value}
            disabled={row.id === user?.id}
            onChange={(e) => updateUserRole(row, e.target.value)}
            sx={{
              minWidth: 150,
              borderRadius: 2,
              fontWeight: 600,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: roleTone(value).border,
              },
            }}
          >
            <MenuItem value="user">Platform User</MenuItem>
            <MenuItem value="superadmin">Superadmin</MenuItem>
          </Select>
        ) : (
          <Chip
            label={roleTone(value).label}
            size="small"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              color: roleTone(value).color,
              backgroundColor: roleTone(value).bg,
              border: `1px solid ${roleTone(value).border}`,
            }}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      renderCell: (params) => {
        const row = params.row;
        return isSuperAdmin ? (
          <Tooltip title={params.value === "active" ? "Active" : "Suspended"}>
            <span>
              <Select
                size="small"
                value={params.value || "active"}
                disabled={row.id === user?.id}
                onChange={(e) => updateUserStatus(row, e.target.value === "active")}
                sx={{
                  minWidth: 124,
                  borderRadius: 2,
                  fontWeight: 600,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: statusTone(params.value).border,
                  },
                }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </Select>
            </span>
          </Tooltip>
        ) : (
          <Chip
            label={statusTone(params.value).label}
            size="small"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              color: statusTone(params.value).color,
              backgroundColor: statusTone(params.value).bg,
              border: `1px solid ${statusTone(params.value).border}`,
            }}
          />
        );
      },
    },
    {
      field: "last_login_at",
      headerName: "Last Login",
      minWidth: 180,
      renderCell: (params) => formatActivityTime(params.value),
    },
    {
      field: "last_logout_at",
      headerName: "Last Logout",
      minWidth: 180,
      renderCell: (params) => formatActivityTime(params.value),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      minWidth: 130,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="View details">
            <IconButton size="small" onClick={() => showUserDetail(params.row)}>
              <VisibilityOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit user">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedUser(params.row);
                setOpenDialog(true);
              }}
            >
              <EditOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Box
        sx={{
          background:
            "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #f8fbff 0%, #eef4f8 100%)",
          p: { xs: 0, md: 1 },
        }}
      >
        <AdminHeader title="Users" />

        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            mb: 4,
            mt: { xs: 2, md: 3 },
            mx: { xs: 1.5, md: 0.5 },
            px: { xs: 2, md: 3 },
            py: { xs: 3, md: 4 },
            borderRadius: 4,
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            boxShadow: "0 30px 80px rgba(15, 23, 42, 0.25)",
          }}
        >
          <Stack spacing={1.1}>
            <Typography
              sx={{
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: "-0.03em",
                color: "#fff",
                fontSize: {
                  xs: 28,
                  // sm: "2.2rem",
                  md: 40,
                },
              }}
            >
              User Directory
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#fff", maxWidth: 720, whiteSpace: { xs: "normal", md: "nowrap" } }}
            >
              Manage platform users, superadmin access, activity, passwords,
              and organization membership visibility.
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ mt: 2 }}
          >
            <Button
              variant="outlined"
              startIcon={<RefreshRounded />}
              onClick={fetchUsers}
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 700,
                borderColor: "#fff",
                color: "#fff",
              }}
            >
              Refresh
            </Button>
            {isSuperAdmin && (
              <Button
                variant="contained"
                disableElevation
                startIcon={<AddRounded />}
                onClick={() => {
                  setSelectedUser(null);
                  setOpenDialog(true);
                }}
                sx={{
                  px: 2.2,
                  py: 1.1,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  color: "#082F49",
                  background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                  boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                }}
              >
                Add User
              </Button>
            )}
          </Stack>

          <Box
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr 1fr",
            }}
            gap={2}
            mt={2}
          >
            {[
              {
                title: "Total Users",
                value: stats.total,
                icon: <PersonRounded color="action" />,
                bg: "#f1f5f9",
              },
              {
                title: "Superadmins",
                value: stats.superadmins,
                icon: <AdminPanelSettingsRounded sx={{ color: "#6d28d9" }} />,
                bg: "#f5f3ff",
              },
              {
                title: "Active Users",
                value: stats.active,
                icon: <VerifiedRounded sx={{ color: "#065f46" }} />,
                bg: "#ecfdf5",
              },
              {
                title: "Suspended Users",
                value: stats.suspended,
                icon: <BlockRounded sx={{ color: "#dc2626" }} />,
                bg: "#fff5f5",
              },
            ].map((s, i) => (
              <Card
                key={i}
                elevation={0}
                sx={{
                  borderRadius: 4,
                  p: 2,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  backdropFilter: "blur(12px)",
                  color: "#fff",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 15px 40px rgba(0,0,0,0.25)",
                  },
                }}
              >
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2,
                      bgcolor: s.bg,
                      mr: 2,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {s.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.7)",
                        fontWeight: 500,
                      }}
                    >
                      {s.title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {Math.max(0, s.value).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            ))}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: { xs: 0, md: 0 } }}>
            <Box
              sx={{
                p: { xs: 2, md: 2.5 },
                background:
                  "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "4",
              }}
            >
              <Stack
                direction={{ xs: "column", xl: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", xl: "center" }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: "-0.02em",
                      color: "#fff",
                      xs: 15,
                      md: 20
                    }}
                  >
                    Platform Access
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(203,213,225,0.75)" }}
                  >
                    Search and filter users by email, lifecycle state, or
                    global role.
                  </Typography>
                </Box>

                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    sx={{
                      minWidth: 160,
                      "& .MuiSelect-icon": { color: "#fff" },
                      "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                      "& .MuiInputLabel-root": {
                        color: "rgba(203,213,225,0.7)",
                      },
                       "& .MuiInputLabel-root.Mui-focused": {
                        color: "rgba(20,184,166,1)", 
                      },
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",

                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.3)",
                        },

                        "&:hover fieldset": {
                          borderColor: "rgba(148,163,184,0.45)",
                        },

                        "&.Mui-focused fieldset": {
                          borderColor: "rgba(20,184,166,0.6)",
                        },
                      },
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Global role"
                    value={filters.global_role}
                    onChange={(e) =>
                      handleFilterChange("global_role", e.target.value)
                    }
                    sx={{
                      minWidth: 180,
                      "& .MuiSelect-icon": { color: "#fff" },
                      "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                      "& .MuiInputLabel-root": {
                        color: "rgba(203,213,225,0.7)",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "rgba(20,184,166,1)", 
                      },
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",

                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.3)",
                        },

                        "&:hover fieldset": {
                          borderColor: "rgba(148,163,184,0.45)",
                        },

                        "&.Mui-focused fieldset": {
                          borderColor: "rgba(20,184,166,0.6)",
                        },
                      },
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="user">Platform User</MenuItem>
                    <MenuItem value="superadmin">Superadmin</MenuItem>
                  </TextField>
                  {(filters.email || filters.status || filters.global_role) && (
                    <Button
                      size="small"
                      onClick={resetFilters}
                      sx={{
                        textTransform: "none",
                        fontSize: 12,
                        height: 38,
                        color: "#fff",
                        border: "1px solid rgba(255,255,255,0.4)",
                        borderRadius: 2,
                        "&:hover": {
                          borderColor: "#fff",
                          backgroundColor: "rgba(255,255,255,0.08)",
                        },
                      }}
                    >
                      Clear filters
                    </Button>
                  )}
                  <TextField
                    size="small"
                    placeholder="Search email"
                    value={filters.email}
                    onChange={(e) => handleFilterChange("email", e.target.value)}
                    sx={{
                      minWidth: { xs: "100%", md: 300 },
                      "& .MuiOutlinedInput-root": {
                        color: "#e2f8f5",
                        borderRadius: 2,
                        "& fieldset": {
                          borderColor: "rgba(148,163,184,0.25)",
                        },
                        "&:hover fieldset": {
                          borderColor: "rgba(148,163,184,0.45)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "rgba(20,184,166,0.6)",
                        },
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "rgba(148, 163, 184, 0.55)",
                        opacity: 1,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded
                            fontSize="small"
                            sx={{ color: "rgba(203,213,225,0.6)" }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: filters.email ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => handleFilterChange("email", "")}
                            sx={{
                              color: "rgba(203,213,225,0.6)",
                              p: 0.5,
                              "&:hover": {
                                color: "#e2f8f5",
                                background: "rgba(255,255,255,0.08)",
                              },
                            }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ height: 620, width: "100%" }}>
              <DataGrid
                rows={users}
                columns={columns}
                loading={loading}
                getRowId={(row) => row.id}
                pageSizeOptions={[15, 20, 25, 50, 100]}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                rowCount={rowCount}
                disableColumnMenu
                disableRowSelectionOnClick
                rowHeight={66}
                sx={{
                  border: 0,
                  "--DataGrid-overlayHeight": "280px",
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#fbfcfd",
                    borderBottom: "1px solid rgba(17, 24, 39, 0.08)",
                    fontSize: 13,
                    fontWeight: 800,
                    color: "#475467",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 800,
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid rgba(17, 24, 39, 0.06)",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-row": {
                    cursor: "default",
                    transition:
                      "background-color 160ms ease, transform 160ms ease",
                    "&:hover": {
                      backgroundColor: "#f8fafc",
                    },
                  },
                  "& .MuiDataGrid-footerContainer": {
                    borderTop: "1px solid rgba(17, 24, 39, 0.08)",
                  },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Stack
                      sx={{ height: "100%" }}
                      alignItems="center"
                      justifyContent="center"
                      spacing={1}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        No platform users found
                      </Typography>
                      <Typography color="text.secondary">
                        Try clearing filters or create a new user.
                      </Typography>
                    </Stack>
                  ),
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <AddUserModel
        open={openDialog}
        onClose={() => {
          if (saving) return;
          setOpenDialog(false);
          setSelectedUser(null);
        }}
        onSubmit={handleSaveUser}
        initialData={selectedUser}
      />

      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 520 }, p: 0 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 3,
            py: 2,
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            color: "#fff",
            width: "100%",
          }}
        >
          <Typography variant="h6">User Details</Typography>
          <IconButton onClick={() => setDetailOpen(false)} sx={{ color: "inherit" }}>
            <Close />
          </IconButton>
        </Stack>

        {/* <Divider sx={{ my: 2 }} /> */}

        {detailLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : userDetail ? (
          <Stack spacing={2}>
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Identity
              </Typography>
              <Typography fontWeight={700}>{userDetail.name || "-"}</Typography>
              <Typography color="text.secondary">{userDetail.email}</Typography>
              <Typography variant="body2" color="text.secondary">
                Firebase UID: {userDetail.firebase_uid}
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
              <Chip
                label={
                  userDetail.global_role === "superadmin"
                    ? "Superadmin"
                    : "Platform User"
                }
                color={
                  userDetail.global_role === "superadmin"
                    ? "secondary"
                    : "default"
                }
              />
              <Chip
                label={userDetail.status === "active" ? "Active" : "Suspended"}
                color={userDetail.status === "active" ? "success" : "default"}
              />
            </Stack>
              </Typography>
            </Box>

            <Divider />

            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Activity
              </Typography>
              <Typography>Created: {formatActivityTime(userDetail.created_at)}</Typography>
              <Typography>
                Last login: {formatActivityTime(userDetail.last_login_at)}
              </Typography>
              <Typography>
                Last logout: {formatActivityTime(userDetail.last_logout_at)}
              </Typography>
            </Box>

            <Divider />

            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Global Permissions
              </Typography>
              {userDetail.global_permissions?.length ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {userDetail.global_permissions.map((permission) => (
                    <Chip key={permission} label={permission} size="small" />
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">No global permissions</Typography>
              )}
            </Box>

            <Divider />

            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Organization Memberships
              </Typography>
              {userDetail.organizations?.length ? (
                <Stack spacing={1.5}>
                  {userDetail.organizations.map((membership) => (
                    <Box
                      key={membership.organization.id}
                      sx={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        p: 1.5,
                      }}
                    >
                      <Typography fontWeight={700}>
                        {membership.organization.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {membership.organization.code || "No code"} ·{" "}
                        {membership.organization.type || "No type"} ·{" "}
                        {membership.membership_status}
                      </Typography>
                      <Chip
                        label={membership.org_role}
                        size="small"
                        sx={{ mt: 1, mb: 1 }}
                      />
                      {membership.permissions?.length ? (
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {membership.permissions.map((permission) => (
                            <Chip
                              key={permission}
                              label={permission}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No org permissions
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">
                  No active organization memberships
                </Typography>
              )}
            </Box>
          </Stack>
        ) : null}
      </Drawer>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={3000}
        onClose={() => setSuccess("")}
        message={success}
      />
    </>
  );
}
